import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, BarChart4, MapPin, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { getYearRange, getYearCount, getLatestYear } from '@/config/years';
import { getStatsSummary } from '@/lib/data';
import { Skeleton } from '@/components/ui/skeleton';
import { useFeeCategories } from '@/contexts/FeeCategoryContext';

const StatsSummary: React.FC = () => {
  const { enabledCategories } = useFeeCategories();
  const [stats, setStats] = useState<Awaited<ReturnType<typeof getStatsSummary>> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showFootnote, setShowFootnote] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    getStatsSummary(enabledCategories).then(setStats).catch(console.error).finally(() => setIsLoading(false));
  }, [enabledCategories]);
  
  const percentDiff = stats && stats.lowest.feeAmount > 0
    ? ((stats.highest.feeAmount - stats.lowest.feeAmount) / stats.lowest.feeAmount) * 100
    : 0;
  const krDiff = stats ? stats.highest.feeAmount - stats.lowest.feeAmount : 0;
  
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-red-100 flex items-center gap-4">
          <div className="rounded-xl bg-red-50 p-3 shrink-0">
            <TrendingUp className="h-6 w-6 text-red-600" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium text-warmGray uppercase tracking-wide">Høyeste</p>
            {isLoading ? (
              <Skeleton className="h-6 w-28 mt-1" />
            ) : (
              <>
                <p className="text-base font-semibold truncate" title={stats?.highest.municipalityName}>
                  {stats?.highest.municipalityName}
                </p>
                <p className="text-sm font-medium text-red-700">
                  {stats?.highest.feeAmount.toLocaleString('nb-NO')} kr/år
                </p>
              </>
            )}
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-100 flex items-center gap-4">
          <div className="rounded-xl bg-slate-50 p-3 shrink-0">
            <TrendingDown className="h-6 w-6 text-slate-600" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium text-warmGray uppercase tracking-wide">Laveste</p>
            {isLoading ? (
              <Skeleton className="h-6 w-28 mt-1" />
            ) : (
              <>
                <p className="text-base font-semibold truncate" title={stats?.lowest.municipalityName}>
                  {stats?.lowest.municipalityName}
                </p>
                <p className="text-sm font-medium text-slate-700">
                  {stats?.lowest.feeAmount.toLocaleString('nb-NO')} kr/år
                </p>
              </>
            )}
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-amber-100 flex items-center gap-4">
          <div className="rounded-xl bg-amber-50 p-3 shrink-0">
            <BarChart4 className="h-6 w-6 text-amber-600" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium text-warmGray uppercase tracking-wide">Snittøkning</p>
            {isLoading ? (
              <Skeleton className="h-6 w-20 mt-1" />
            ) : (
              <>
                {stats?.avgIncreaseLastYear != null && (
                  <p className="text-base font-semibold">{stats.avgIncreaseLastYear.toFixed(1)}% siste år</p>
                )}
                <p className={`text-sm ${stats?.avgIncreaseLastYear != null ? 'text-warmGray' : 'font-semibold'}`}>
                  {stats?.avgIncrease.toFixed(1)}% siste {getYearCount()} år
                </p>
              </>
            )}
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-100 flex items-center gap-4">
          <div className="rounded-xl bg-slate-50 p-3 shrink-0">
            <MapPin className="h-6 w-6 text-slate-600" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium text-warmGray uppercase tracking-wide">Snitt & antall</p>
            {isLoading ? (
              <Skeleton className="h-6 w-24 mt-1" />
            ) : (
              <>
                <p className="text-base font-semibold">
                  {stats?.avgFee.toLocaleString('nb-NO', { maximumFractionDigits: 0 })} kr/år
                </p>
                <p className="text-sm text-warmGray">{stats?.municipalityCount} kommuner</p>
              </>
            )}
          </div>
        </div>
      </div>
      
      {!isLoading && stats && (
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="grid sm:grid-cols-2 gap-4 text-sm text-warmGray">
            <p className="leading-relaxed">
              Den dyreste kommunen ({stats.highest.municipalityName}) har ca. {percentDiff.toFixed(0)}% 
              høyere avgifter enn den billigste ({stats.lowest.municipalityName}). 
              Forskjellen utgjør <strong className="text-foreground">{krDiff.toLocaleString('nb-NO')} kr/år</strong> for en standardbolig.
            </p>
            <p className="leading-relaxed">
              Lands gjennomsnitt er {stats.avgFee.toLocaleString('nb-NO', { maximumFractionDigits: 0 })} kr/år. 
              Kommunale avgifter har i snitt økt med <strong className="text-foreground">{stats.avgIncrease.toFixed(1)}%</strong> de siste {getYearCount()} årene ({getYearRange()})
              {stats.avgIncreaseLastYear != null && (
                <> og <strong className="text-foreground">{stats.avgIncreaseLastYear.toFixed(1)}%</strong> fra {getLatestYear() - 1} til {getLatestYear()}</>
              )}.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowFootnote(!showFootnote)}
            className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <Info className="h-3.5 w-3.5" />
            Om tallene
            {showFootnote ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
          {showFootnote && (
            <p className="mt-2 pt-2 border-t border-border/50 text-xs text-warmGray leading-relaxed">
              Nøkkeltallene oppdateres automatisk og kan inneholde estimerte verdier for kommuner hvor faktiske historiske data mangler.
              Estimerte verdier er basert på nyeste tilgjengelige data.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default StatsSummary;
